// TODO: https://product-fabric.atlassian.net/browse/DSP-3792
// Use tokens to all the color in this file for the echart

import { colors } from '@atlaskit/theme';
import type { IntlShape } from '@atlassian/jira-intl';
import makeDateFormatter from '@atlassian/jira-software-report-charting-common/src/common/utils/make-date-formatter/index.tsx';
import { SCOPE_SCATTER_SERIES, COMPLETION_SCATTER_SERIES } from '../../../common/constants';
import commonMessages from '../../../common/messages';
import type { BurnupChartData, SprintReportsEstimationStatistic } from '../../../common/types';
import {
	type SprintEvent,
	buildEvents,
	getScope,
	getCompletion,
} from '../../../common/utils/sprint-event';
import getCompletedWorkSeries from './get-completed-work-series';
import getGuideline, { getLastKnownSprintDate } from './get-guideline';
import getScatterSeries, { hasDeltaCompletion, hasDeltaScope } from './get-scatter-series';
import getScopeChangeSeries from './get-scope-change-line';
import renderStatisticField from './render-statistic-field';

type OptionsInput = {
	burnup: BurnupChartData;
	isMouseOver: boolean;
	statisticField: null | SprintReportsEstimationStatistic;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderTooltip: (params?: any) => string;
	now: Date;
	intl: IntlShape;
	userTimeZone: string;
	prefersReducedMotion: boolean;
};

/**
 * Generate ECharts configuration.
 */
export const getOptions = ({
	burnup,
	isMouseOver,
	renderTooltip,
	now,
	intl,
	statisticField,
	userTimeZone,
	prefersReducedMotion, // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: OptionsInput): any => {
	const sprintEvents: SprintEvent[] = buildEvents(now, burnup);
	const minDate = new Date(burnup.sprintStartEvent.timestamp);
	const maxDate = getLastKnownSprintDate(burnup, now);

	const series = [
		getScatterSeries(
			isMouseOver,
			sprintEvents,
			SCOPE_SCATTER_SERIES,
			colors.R400,
			getScope,
			hasDeltaScope,
		),
		getScatterSeries(
			isMouseOver,
			sprintEvents,
			COMPLETION_SCATTER_SERIES,
			colors.G400,
			getCompletion,
			hasDeltaCompletion,
		),
		getGuideline(burnup, now, maxDate, sprintEvents),
		getCompletedWorkSeries(burnup, sprintEvents, now, maxDate),
		getScopeChangeSeries(burnup, now, maxDate),
	];

	return {
		animation: !prefersReducedMotion,
		xAxis: {
			name: intl.formatMessage(commonMessages.time),
			nameLocation: 'center',
			nameGap: 50,
			type: 'time',
			max: maxDate,
			min: minDate,
			nameTextStyle: {
				color: '#707070',
				fontSize: 14,
				fontWeight: 500,
			},
			splitLine: {
				show: false,
			},
			boundaryGap: false,
			axisLine: {
				lineStyle: {
					color: '#c0c0c1',
				},
			},
			axisLabel: {
				color: '#545454',
				formatter: makeDateFormatter({
					userTimeZone,
					min: minDate.getTime(),
					max: maxDate.getTime(),
					ticks: 5,
				}),
				showMinLabel: false,
				showMaxLabel: false,
			},
		},
		yAxis: {
			splitLine: {
				show: false,
			},
			axisLine: {
				lineStyle: {
					color: '#c0c0c1',
				},
			},
			axisPointer: {
				snap: true,
			},
			axisLabel: {
				color: '#545454',
				showMaxLabel: false,
			},
			axisTick: {
				show: true,
			},
			nameLocation: 'center',
			nameGap: 56,
			nameTextStyle: {
				color: '#707070',
				fontSize: 14,
				fontWeight: 500,
			},
			name: statisticField ? renderStatisticField(statisticField, intl) : null,
			type: 'value',
		},
		grid: {
			show: true,
			borderColor: '#c0c0c1',
			top: 28,
			left: 72,
			right: 40,
			bottom: 96,
		},
		tooltip: {
			show: true,
			transitionDuration: 0,
			extraCssText: 'overflow: hidden; box-shadow: 0 0 8px rgba(0, 0, 0, 0.3)',
			backgroundColor: 'white',
			textStyle: {
				color: 'rgb(66, 82, 110)',
			},
			trigger: 'item',
			padding: 0,
			formatter: renderTooltip,
		},
		series,
	};
};
